import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { PlusIcon } from '@heroicons/react/24/outline';
import { PlusCircleIcon } from '@heroicons/react/24/solid';
import CardTodoList, { CardTodoListDraggable } from './viewComponents/CardTodoList';
import CardTodoListAdd from './viewComponents/CardTodoListAdd';
import CalendarFilterDropdown, {
  CALENDAR_FILTER_DROPDOWN_WRAP_CLASSNAME,
} from './viewComponents/CalendarFilterDropdown';
import DropZone from './viewComponents/DropZone';
import SpinnerLoad from '../moduleComponents/SpinnerLoad';
import { getTodo, createTodo, deleteTodo, reorderTodo } from '../moduleServices/planner_todos_api';
import { useTodoDrag } from './viewServices/useTodoDrag';
import { useTodoItems } from './viewServices/useTodoItems';
import { useTodoToggle } from './viewServices/useTodoToggle';
import { useDevice } from 'services/DeviceContext';
import { useResponsiveDrag } from 'services/useResponsiveDrag';
import {
  loadMultiCalendarFilterFromStorage,
  persistMultiCalendarFilter,
  coerceMultiCalendarFilter,
  multiCalendarFiltersEqual,
} from '../moduleServices/planner_calendar_multi_filter';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';

const STORAGE_KEY_CALENDAR_FILTER = 'planner_todo_calendar_filter';

function getStoredCalendarFilter() {
  return loadMultiCalendarFilterFromStorage(STORAGE_KEY_CALENDAR_FILTER);
}

export default function MainTodo({ calendars = [] }) {
  const [todo, setTodo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deletingTodo, setDeletingTodo] = useState({});
  const [showNewTodoForm, setShowNewTodoForm] = useState(false);
  const [selectedCalendarIds, setSelectedCalendarIdsState] = useState(getStoredCalendarFilter);

  const setSelectedCalendarIds = useCallback((ids) => {
    setSelectedCalendarIdsState(ids);
    persistMultiCalendarFilter(STORAGE_KEY_CALENDAR_FILTER, ids);
  }, []);

  useEffect(() => {
    const next = coerceMultiCalendarFilter(selectedCalendarIds, calendars);
    if (multiCalendarFiltersEqual(selectedCalendarIds, next)) return;
    setSelectedCalendarIds(next);
  }, [calendars, selectedCalendarIds, setSelectedCalendarIds]);

  // ##### Custom hooks
  const { activeTodoId, handleDragStart, handleDragEnd, sensors } = useTodoDrag(todo, setTodo);
  const { loadingTodo, addItem, editItem, deleteItem, toggleItem, reorderItems } = useTodoItems(todo, setTodo);
  const { handleCardToggle } = useTodoToggle(todo, setTodo);
  const { isMobile } = useDevice();
  const { containerClass, columnClass, taskCardClass } = useResponsiveDrag();

  const todosForTodoTypeCalendars = useMemo(() => {
    const allowed = new Set(calendars.map((c) => c.id));
    return todo.filter((t) => allowed.has(t.calendar?.id ?? NaN));
  }, [todo, calendars]);

  // ##### Filter by selected calendars (null = show all scoped to-do calendars)
  const filteredTodo = useMemo(() => {
    if (selectedCalendarIds === null) return todosForTodoTypeCalendars;
    if (selectedCalendarIds.length === 0) return [];
    const allowed = new Set(selectedCalendarIds);
    return todosForTodoTypeCalendars.filter((t) => allowed.has(t.calendar?.id ?? NaN));
  }, [todosForTodoTypeCalendars, selectedCalendarIds]);

  const defaultTodoCalendarId = useMemo(() => {
    if (selectedCalendarIds == null) return null;
    if (selectedCalendarIds.length === 0) return null;
    return selectedCalendarIds[0];
  }, [selectedCalendarIds]);

  // ##### Separate to-do's by column
  const separateTodoByColumn = (todoList) => {
    const left = todoList.filter(t => t.column === 0).sort((a, b) => a.order - b.order);
    const right = todoList.filter(t => t.column === 1).sort((a, b) => a.order - b.order);
    return { left, right };
  };

  const { left: leftColumnTodo, right: rightColumnTodo } = separateTodoByColumn(filteredTodo);

  useEffect(() => {
    setLoading(true);
    setError(null);
    getTodo().then(data => {
      // Ensure all to-do's have column and order fields
      const todoWithDefaults = data.map((todo, index) => ({
        ...todo,
        column: todo.column !== undefined ? todo.column : (index % 2), // Alternate columns if not set
        order: todo.order !== undefined ? todo.order : index
      }));
      setTodo(todoWithDefaults);
      setLoading(false);
    }).catch(err => {
      console.error('Failed to load to-do\'s:', err);
      setError('Failed to load to-do\'s. Please check your connection.');
      setLoading(false);
    });
  }, []);

  const handleCreateTodo = async (formData) => {
    setSubmitting(true);
    try {
      const newTodo = await createTodo({
        ...formData,
        column: 0, // Always add to left column
        order: 0 // Always add to beginning of left column
      });
      
      // Update existing to-do's order to make room for new to-do at position 0
      const updatedTodo = todo.map(todo => {
        if (todo.column === 0) {
          return { ...todo, order: todo.order + 1 };
        }
        return todo;
      });
      
      // Add new to-do at the beginning
      const finalTodo = [newTodo, ...updatedTodo];
      setTodo(finalTodo);
      
      // Update backend with new order
      try {
        await reorderTodo(finalTodo);
        console.log('To-do\'s reordered successfully after creation');
      } catch (reorderError) {
        console.error('Error reordering to-do\'s after creation:', reorderError);
        // Don't show error to user as to-do was created successfully
      }
      
      setShowNewTodoForm(false);
      toast.success('To-Do created successfully');
    } catch (error) {
      toast.error('Failed to create to-do');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelNewTodo = () => {
    setShowNewTodoForm(false);
  };

  // ##### Handle todo update (for title/date updates)
  const handleUpdateTodo = (updatedTodo) => {
    setTodo(prev => prev.map(t => 
      t.id === updatedTodo.id ? { ...t, ...updatedTodo } : t
    ));
  };


  const handleDeleteTodo = async (todoId) => {
    const todoItem = todo.find(t => t.id === todoId);
    if (!todoItem) return;

    // Set deleting state
    setDeletingTodo(prev => ({ ...prev, [todoId]: true }));

    try {
      await deleteTodo(todoId);
      // Remove from to-do's after successful deletion
      setTodo(prev => prev.filter(t => t.id !== todoId));
      toast.success('To-Do deleted successfully');
    } catch (error) {
      toast.error('Failed to delete to-do');
    } finally {
      // Clear deleting state
      setDeletingTodo(prev => {
        const newState = { ...prev };
        delete newState[todoId];
        return newState;
      });
    }
  };

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="text-red-600 text-lg font-medium">{error}</div>
        <button 
          onClick={() => window.location.reload()} 
          className="px-4 py-2 bg-blue-700 text-white rounded hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      {!loading && (
        <div className="flex w-full justify-end px-2 mt-2 pb-0">
          <div className={CALENDAR_FILTER_DROPDOWN_WRAP_CLASSNAME}>
            <CalendarFilterDropdown
              multiple
              fullWidthTrigger={false}
              calendars={calendars}
              value={selectedCalendarIds}
              onChange={setSelectedCalendarIds}
            />
          </div>
        </div>
      )}
      {loading ? (
        <div className="py-8">
          <SpinnerLoad />
        </div>
      ) : filteredTodo.length === 0 && todosForTodoTypeCalendars.length > 0 ? (
        <div className="py-8 text-center text-gray-500 text-sm">
          No to-dos in this calendar.
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className={`mt-2 ${containerClass}`}>
            {isMobile ? (
              // ##### MOBILE LAYOUT - Single Column
              <div className="w-full space-y-2">
                {/* Add New To-Do Button - Only when no to-do's exist (any calendar) */}
                {!showNewTodoForm && filteredTodo.length === 0 && todosForTodoTypeCalendars.length === 0 && (
                  <div className="h-28 bg-white rounded-2xl shadow p-0 overflow-hidden flex flex-col border-2 border-dashed border-gray-300 hover:border-blue-400 transition-colors relative">
                    <button
                      className="w-full h-full flex items-center justify-center px-4 py-3 focus:outline-none hover:bg-blue-50 transition"
                      onClick={() => setShowNewTodoForm(true)}
                    >
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <PlusIcon className="w-4 h-4 text-blue-700" />
                        </div>
                        <span className="text-gray-600 font-medium text-sm">Add New To-Do</span>
                      </div>
                    </button>
                  </div>
                )}
                
                {/* New To-Do Form */}
                {showNewTodoForm && (
                  <CardTodoListAdd 
                    calendars={calendars}
                    defaultCalendarId={defaultTodoCalendarId}
                    onSubmit={handleCreateTodo} 
                    submitting={submitting} 
                    isCreating={submitting}
                    onCancel={handleCancelNewTodo}
                  />
                )}
                
                <SortableContext 
                  items={filteredTodo.map(t => t.id)} 
                  strategy={verticalListSortingStrategy}
                >
                  {filteredTodo.map(todo => (
                    <CardTodoListDraggable
                      key={todo.id}
                      todo={todo}
                      calendars={calendars}
                      onAddItem={(text) => addItem(todo.id, text)}
                      onDeleteItem={itemId => deleteItem(todo.id, itemId)}
                      onDeleteTodo={() => handleDeleteTodo(todo.id)}
                      onToggleItem={itemId => toggleItem(todo.id, itemId)}
                      onReorderItems={newItems => reorderItems(todo.id, newItems)}
                      onEditItem={(itemId, newText) => editItem(todo.id, itemId, newText)}
                      onUpdateTodo={handleUpdateTodo}
                      isTodoLoading={loadingTodo[todo.id] || false}
                      isDeleting={deletingTodo[todo.id] || false}
                      isDragging={activeTodoId === todo.id}
                      onCardToggle={handleCardToggle}
                      open={todo.is_expanded}
                    />
                  ))}
                </SortableContext>
              </div>
            ) : (
              // ##### DESKTOP LAYOUT - Two Columns
              <>
                {/* Left Column */}
                <DropZone 
                  id="left-column" 
                  column={0}
                  isActive={false}
                >
                  {/* Add New To-Do Button - Only when no to-do's exist */}
                  {!showNewTodoForm && filteredTodo.length === 0 && todosForTodoTypeCalendars.length === 0 && (
                    <div className="h-28 bg-white rounded-2xl shadow p-0 overflow-hidden flex flex-col border-2 border-dashed border-gray-300 hover:border-blue-400 transition-colors relative">
                      <button
                        className="w-full h-full flex items-center justify-center px-4 py-3 focus:outline-none hover:bg-blue-50 transition"
                        onClick={() => setShowNewTodoForm(true)}
                      >
                        <div className="flex flex-col items-center gap-2">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                            <PlusIcon className="w-4 h-4 text-blue-700" />
                          </div>
                          <span className="text-gray-600 font-medium text-sm">Add New To-Do</span>
                        </div>
                      </button>
                    </div>
                  )}
                  
                  {/* New To-Do Form */}
                  {showNewTodoForm && (
                    <CardTodoListAdd 
                      calendars={calendars}
                      defaultCalendarId={defaultTodoCalendarId}
                      onSubmit={handleCreateTodo} 
                      submitting={submitting} 
                      isCreating={submitting}
                      onCancel={handleCancelNewTodo}
                    />
                  )}
                  
                  <SortableContext 
                    items={leftColumnTodo.map(t => t.id)} 
                    strategy={verticalListSortingStrategy}
                  >
                    {leftColumnTodo.map(todo => (
                      <CardTodoListDraggable
                        key={todo.id}
                        todo={todo}
                        calendars={calendars}
                        onAddItem={(text) => addItem(todo.id, text)}
                        onDeleteItem={itemId => deleteItem(todo.id, itemId)}
                        onDeleteTodo={() => handleDeleteTodo(todo.id)}
                        onToggleItem={itemId => toggleItem(todo.id, itemId)}
                        onReorderItems={newItems => reorderItems(todo.id, newItems)}
                        onEditItem={(itemId, newText) => editItem(todo.id, itemId, newText)}
                        onUpdateTodo={handleUpdateTodo}
                        isTodoLoading={loadingTodo[todo.id] || false}
                        isDeleting={deletingTodo[todo.id] || false}
                        isDragging={activeTodoId === todo.id}
                        onCardToggle={handleCardToggle}
                        open={todo.is_expanded}
                      />
                    ))}
                  </SortableContext>
                </DropZone>
                
                {/* Right Column */}
                <DropZone 
                  id="right-column" 
                  column={1}
                  isActive={false}
                >
                  <SortableContext 
                    items={rightColumnTodo.map(t => t.id)} 
                    strategy={verticalListSortingStrategy}
                  >
                    {rightColumnTodo.map(todo => (
                      <CardTodoListDraggable
                        key={todo.id}
                        todo={todo}
                        calendars={calendars}
                        onAddItem={(text) => addItem(todo.id, text)}
                        onDeleteItem={itemId => deleteItem(todo.id, itemId)}
                        onDeleteTodo={() => handleDeleteTodo(todo.id)}
                        onToggleItem={itemId => toggleItem(todo.id, itemId)}
                        onReorderItems={newItems => reorderItems(todo.id, newItems)}
                        onEditItem={(itemId, newText) => editItem(todo.id, itemId, newText)}
                        onUpdateTodo={handleUpdateTodo}
                        isTodoLoading={loadingTodo[todo.id] || false}
                        isDeleting={deletingTodo[todo.id] || false}
                        isDragging={activeTodoId === todo.id}
                        onCardToggle={handleCardToggle}
                        open={todo.is_expanded}
                      />
                    ))}
                  </SortableContext>
                </DropZone>
              </>
            )}
          </div>

          <DragOverlay>
            {activeTodoId ? (
              <CardTodoList
                todo={todo.find(t => t.id === activeTodoId)}
                calendars={calendars}
                onAddItem={() => {}}
                onDeleteItem={() => {}}
                onDeleteTodo={() => {}}
                onToggleItem={() => {}}
                onReorderItems={() => {}}
                onEditItem={() => {}}
                isTodoLoading={false}
                isDeleting={false}
                open={todo.find(t => t.id === activeTodoId)?.is_expanded ?? true}
                onCardToggle={() => {}}
                isEditMode={false}
                onEditModeToggle={() => {}}
                onDeleteClick={() => {}}
                onCancelDelete={() => {}}
                onConfirmDelete={() => {}}
                showDeleteModal={false}
              />
            ) : null}
          </DragOverlay>
        </DndContext>
      )}
      
      {/* FAB Button - Only when to-do's exist and form is not showing */}
      {!loading && todosForTodoTypeCalendars.length > 0 && !showNewTodoForm && (
        <button
          className="fixed bottom-20 right-6 bg-blue-700 hover:bg-blue-800 text-white p-3 rounded-full shadow-lg"
          onClick={() => setShowNewTodoForm(true)}
          aria-label="Add New To-Do"
        >
          <PlusCircleIcon className="h-8 w-8" />
        </button>
      )}
    </div>
  );
}
